import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api, ENTITY_STATUS_LABELS } from '@/lib/api';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_authed/businesses/')({
  component: BusinessesListPage,
});

const createSchema = z.object({
  name: z.string().min(1).max(200),
  // Optional: left blank, the API generates one from the name.
  code: z.preprocess(
    (v) => (typeof v === 'string' ? v.trim().toUpperCase() : v),
    z.string().regex(/^([A-Z0-9-]{2,32})?$/u, '2–32 uppercase letters, digits, hyphens'),
  ),
  contactEmail: z.string().email().optional().or(z.literal('')),
  mapUrl: z.string().url().optional().or(z.literal('')),
});

type CreateValues = z.infer<typeof createSchema>;

function BusinessesListPage() {
  const queryClient = useQueryClient();
  const bizQ = useQuery({ queryKey: ['businesses'], queryFn: api.listBusinesses });
  const villagesQ = useQuery({ queryKey: ['villages'], queryFn: () => api.listVillages() });
  const villageName = (id: string | null) =>
    id ? (villagesQ.data?.villages.find((v) => v.id === id)?.name.en ?? id) : null;

  const create = useMutation({
    mutationFn: (v: CreateValues) =>
      api.createBusiness({
        name: v.name,
        code: v.code || undefined,
        contactEmail: v.contactEmail || null,
        mapUrl: v.mapUrl || null,
        villageId: null,
        address: null,
        logoUrl: null,
        notes: null,
        status: 'active',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
    },
  });

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-h1">Businesses</h1>
        <p className="mt-1 text-body text-slate-gray">
          Local businesses: partners that honour perks at the till, and the ones marked public
          appear on their village's page on the website.
        </p>
      </header>

      <CreateBusinessForm
        onSubmit={(v) => create.mutateAsync(v)}
        creating={create.isPending}
        error={create.error ? String(create.error) : null}
      />

      <section>
        <h2 className="mb-3 text-h2">All businesses</h2>
        {bizQ.isLoading ? (
          <div className="card text-slate-gray">Loading…</div>
        ) : bizQ.data && bizQ.data.businesses.length > 0 ? (
          <div className="overflow-x-auto rounded-card border border-neutral-gray bg-soft-white">
            <table className="w-full text-left text-body">
              <thead className="bg-light-gray text-caption uppercase text-slate-gray">
                <tr>
                  <th className="px-4 py-2">Code</th>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Contact</th>
                  <th className="px-4 py-2">Village</th>
                  <th className="px-4 py-2">Website</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bizQ.data.businesses.map((b) => (
                  <tr key={b.id} className="border-t border-neutral-gray/60">
                    <td className="px-4 py-2 font-mono text-caption text-dark-navy">{b.code}</td>
                    <td className="px-4 py-2 text-dark-navy">{b.name}</td>
                    <td className="px-4 py-2 text-slate-gray">
                      {b.contactEmail ?? <span className="text-slate-gray/60">—</span>}
                    </td>
                    <td className="px-4 py-2 text-slate-gray">
                      {villageName(b.villageId) ?? (
                        <span className="text-slate-gray/60">— not assigned —</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-caption">
                      {!b.isPublic ? (
                        <span className="text-slate-gray/60">Hidden</span>
                      ) : b.villageId ? (
                        <span className="text-success">Published</span>
                      ) : (
                        // Published without a village is the one combination
                        // that looks fine in the CMS and shows nothing on the
                        // site, so it gets said out loud.
                        <span className="text-warning">Needs a village</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={cn(
                          'inline-block rounded-full px-2 py-0.5 text-caption font-semibold',
                          b.status === 'active' && 'bg-success/15 text-success',
                          b.status === 'inactive' && 'bg-warning/15 text-warning',
                          b.status === 'archived' && 'bg-light-gray text-slate-gray',
                        )}
                      >
                        {ENTITY_STATUS_LABELS[b.status]}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Link
                        to="/businesses/$businessId"
                        params={{ businessId: b.id }}
                        className="btn-ghost"
                      >
                        Open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card text-slate-gray">No businesses yet. Create one above.</div>
        )}
      </section>
    </div>
  );
}

function CreateBusinessForm({
  onSubmit,
  creating,
  error,
}: {
  onSubmit: (v: CreateValues) => Promise<unknown>;
  creating: boolean;
  error: string | null;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateValues>({ resolver: zodResolver(createSchema) });

  const submit = async (v: CreateValues) => {
    await onSubmit(v);
    reset();
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="card space-y-4">
      <h2 className="text-h2">New business</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="label">Name</label>
          <input className="input" placeholder="Monastery Shop" {...register('name')} />
        </div>
        <div>
          <label className="label">Internal code (optional — generated if left blank)</label>
          <input
            className="input font-mono uppercase"
            placeholder="MON-7K9P"
            {...register('code')}
          />
          {errors.code ? (
            <p className="mt-1 text-caption text-error">{errors.code.message}</p>
          ) : null}
        </div>
        <div>
          <label className="label">Contact email (optional)</label>
          <input className="input" {...register('contactEmail')} />
        </div>
        <div>
          <label className="label">Google Maps pin URL (optional)</label>
          <input
            className="input"
            placeholder="https://maps.app.goo.gl/…"
            {...register('mapUrl')}
          />
        </div>
      </div>
      {error ? (
        <div className="rounded-lg border border-error/30 bg-error/10 p-3 text-caption text-error">
          {error}
        </div>
      ) : null}
      <button type="submit" className="btn-primary" disabled={creating}>
        {creating ? 'Creating…' : 'Create business'}
      </button>
    </form>
  );
}
